import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const AdminDashboard = () => {
    const [an, setAn] = useState(null);
    const [cleaning, setCleaning] = useState(false);
    const load = () => api.get("/admin/analytics").then((r) => setAn(r.data));
    useEffect(() => { load(); }, []);

    const cleanup = async () => {
        if (!window.confirm("Remove ALL demo/test data (test accounts, TEST campaigns, test leads)? Real users and data are untouched. This cannot be undone.")) return;
        setCleaning(true);
        try {
            const r = await api.post("/admin/cleanup-demo-data");
            const total = Object.values(r.data.removed).reduce((a, b) => a + b, 0);
            toast.success(`Demo data removed — ${total} records purged`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cleanup failed");
        } finally { setCleaning(false); }
    };

    if (!an) return <DashboardShell role="admin" title="Operations"><div className="font-heading">Loading…</div></DashboardShell>;

    const stageData = Object.entries(an.campaigns_by_stage).map(([k, v]) => ({ name: k, value: v }));
    const vettingData = Object.entries(an.creators_by_vetting).map(([k, v]) => ({ name: k, value: v }));
    const PIE_COLORS = ["#0038FF", "#E3185F", "#16161D", "#E9EADC", "#9B9B9B"];

    return (
        <DashboardShell role="admin" title="Operations dashboard" subtitle="Members · creators · campaigns · revenue">
            <div className="flex justify-end">
                <button
                    data-testid="cleanup-demo-btn"
                    onClick={cleanup}
                    disabled={cleaning}
                    className="flex items-center gap-2 text-xs font-bold text-yc-pink border-2 border-yc-pink rounded-full px-4 py-2 hover:bg-yc-pink hover:text-white transition-colors disabled:opacity-50"
                >
                    <Trash2 className="w-3.5 h-3.5" /> {cleaning ? "Removing…" : "Remove demo data"}
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                    { l: "Active members", v: an.active_brands, tid: "metric-active-brands" },
                    { l: "MRR", v: `£${an.mrr_gbp.toFixed(0)}`, tid: "metric-mrr" },
                    { l: "Package revenue", v: `£${an.package_revenue_gbp.toFixed(0)}`, tid: "metric-pkg-rev" },
                    { l: "Matches to confirm", v: an.matches_awaiting_confirmation, tid: "metric-matches" },
                ].map((m) => (
                    <div key={m.l} className="yc-card-sm p-6" data-testid={m.tid}>
                        <div className="yc-overline">{m.l}</div>
                        <div className="font-heading font-black text-5xl tracking-tighter mt-2">{m.v}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="yc-card p-6">
                    <h3 className="font-heading text-xl font-bold">Campaigns by stage</h3>
                    <div className="h-64 mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stageData}>
                                <CartesianGrid stroke="#16161D" strokeOpacity={0.1} />
                                <XAxis dataKey="name" stroke="#16161D" />
                                <YAxis stroke="#16161D" />
                                <Tooltip />
                                <Bar dataKey="value" fill="#0038FF" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="yc-card p-6">
                    <h3 className="font-heading text-xl font-bold">Creators by vetting</h3>
                    <div className="h-64 mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={vettingData} dataKey="value" nameKey="name" outerRadius={90} label>
                                    {vettingData.map((entry, i) => <Cell key={entry.name || `cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="yc-card p-6">
                <h3 className="font-heading text-xl font-bold">Active clients per strategist</h3>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(an.active_clients_per_strategist).length === 0 && <div className="text-yc-ink/60 text-sm">No assigned matches yet.</div>}
                    {Object.entries(an.active_clients_per_strategist).map(([sid, count]) => (
                        <div key={sid} className="border-2 border-yc-ink rounded-2xl p-4 bg-white">
                            <div className="yc-overline">Strategist</div>
                            <div className="font-mono text-sm truncate">{sid}</div>
                            <div className="font-heading font-black text-3xl mt-1">{count}</div>
                            <div className="text-xs text-yc-ink/60">active clients</div>
                        </div>
                    ))}
                </div>
            </div>
        </DashboardShell>
    );
};

export default AdminDashboard;
